import cx from 'classnames';
import {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {capitalizeFirstLetter, pluralizeWord} from '~/common/utils';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import {
  selectActiveFilterGroups,
  selectHasActiveFilters,
  selectSortBy,
} from '~/GalleryPB/reducers/filters';
import {
  selectFilters,
  selectHasFilters,
  selectNumTemplates,
} from '~/GalleryPB/reducers/galleryData';
import {SortByOption} from '~/GalleryPB/types/filters';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Radio} from '~library/atoms/Radio';
import {Typography} from '~library/atoms/Typography';
import {useBrowserWidth} from '~library/hooks/useBrowserWidth';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {FilterGroup} from './FilterGroup';
import * as styles from './FilterManager.module.scss';

const DEFAULT_OPEN_GROUPS = ['free_premium', 'show_only'];
const SCROLL_AFFORDANCE = 8;

interface FilterManagerContentProps {
  onFilterClick: (filterSectionName: string, filterId: string, newState: boolean) => void;
  onSortByChange: (sortBy: SortByOption) => void;
  open?: boolean;
  onClearFilters: () => void;
  onApplyFilters: () => void; // This just closes the filter panel; it's just a user QOL thing
  isLoading?: boolean;
}

export const FilterManagerContent: FC<FilterManagerContentProps> = ({
  onFilterClick,
  onSortByChange,
  open = false,
  onClearFilters,
  onApplyFilters,
  isLoading = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [containerStyle, setContainerStyle] = useState<CSSProperties>();
  const activeFilterGroups = useSelector(selectActiveFilterGroups, shallowEqual);
  const numTemplates = useSelector(selectNumTemplates);
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    new Set([...DEFAULT_OPEN_GROUPS, ...activeFilterGroups])
  );
  const filterGroups = useSelector(selectFilters, shallowEqual);
  const hasActiveFilters = useSelector(selectHasActiveFilters);
  const sortBy = useSelector(selectSortBy);
  const isLargeAndUp = useIsLargeAndUp();
  const browserWidth = useBrowserWidth();
  const {isInUyoOrDyo} = useUyoOrDyo();

  const handleGroupToggle = useCallback((groupName: string) => {
    setOpenGroups((prevOpenGroups) => {
      const newOpenGroups = new Set(prevOpenGroups);
      if (newOpenGroups.has(groupName)) {
        newOpenGroups.delete(groupName);
      } else {
        newOpenGroups.add(groupName);
      }
      return newOpenGroups;
    });
  }, []);

  const toggleScrollTeaser = useCallback(() => {
    if (!containerRef.current || !footerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    const scrollHeight = containerRef.current.scrollHeight;
    const clientHeight = containerRef.current.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - SCROLL_AFFORDANCE) {
      footerRef.current.classList.add(styles.filterManagerFooterHideShadow);
    } else {
      footerRef.current.classList.remove(styles.filterManagerFooterHideShadow);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Run when a filter group is opened or closed
  useEffect(() => {
    if (!open) return;
    setTimeout(() => toggleScrollTeaser(), 350); // wait for animation to finish
    const containerEl = containerRef.current;
    containerEl?.addEventListener('scroll', toggleScrollTeaser);
    return () => {
      containerEl?.removeEventListener('scroll', toggleScrollTeaser);
    };
  }, [openGroups, open, toggleScrollTeaser]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!isLargeAndUp) {
      containerRef.current.style.removeProperty('height');
      if (open) document.body.classList.add('no-scroll');
      else document.body.classList.remove('no-scroll');
    } else {
      const globalNavHeight =
        document.getElementById('meganav-variant-global-nav')?.offsetHeight || 0;
      const galleryHeaderHeight =
        document.getElementById('gallery-results-header')?.offsetHeight || 0;
      containerRef.current.style.height = `calc(100dvh - ${globalNavHeight + galleryHeaderHeight}px)`;
    }
  }, [isLargeAndUp, open]);

  // This runs on first open to ensure the shadow is correct
  useEffect(() => {
    if (!open) return;
    setTimeout(() => toggleScrollTeaser(), 350); // wait for animation to finish
  }, [open, toggleScrollTeaser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We also want to run this when isInUyoOrDyo changes
  useEffect(() => {
    if (!isLargeAndUp) setContainerStyle(undefined);
    const localNavHeight = document.getElementById('meganav-variant-local-nav')?.offsetHeight || 0;
    const galleryResultsHeaderHeight =
      document.getElementById('gallery-results-header')?.offsetHeight || 0;
    setContainerStyle({
      top: `${localNavHeight + galleryResultsHeaderHeight}px`,
    });
  }, [isLargeAndUp, isInUyoOrDyo, browserWidth]);

  const handleApplyClick = useCallback(() => {
    if (!isLoading) onApplyFilters();
  }, [isLoading, onApplyFilters]);

  const ApplyButtonText = useMemo(() => {
    if (isLoading) return <span className="evt-spinner inline" style={{margin: 0}} />;
    return `Show ${numTemplates} ${pluralizeWord('result', numTemplates)}`;
  }, [isLoading, numTemplates]);

  return (
    <>
      <div
        className={styles.filterManager}
        ref={containerRef}
        data-qa-id="galleryFilterManager"
        style={containerStyle}
      >
        {isLargeAndUp ? (
          <div className={styles.filterManagerHeader}>
            <Typography variant="paragraph1" semibold>
              Filters
            </Typography>
            {hasActiveFilters && (
              <Button
                variant="link"
                size="small"
                data-qa-id="galleryFiltersDrawerClearFiltersButton"
                onClick={() => onClearFilters()}
                disableProcessing
              >
                Clear all
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.sortBySection}>
              <Typography variant={isLargeAndUp ? 'paragraph2' : 'paragraph1'} semibold>
                Sort by
              </Typography>
              <div className={styles.sortByOptions}>
                {[SortByOption.MOST_POPULAR, SortByOption.NEWLY_ADDED].map((sortOption) => (
                  <Radio
                    key={sortOption}
                    label={capitalizeFirstLetter(sortOption.replaceAll('_', ' '))}
                    checked={sortBy === sortOption}
                    onChange={() => onSortByChange(sortOption)}
                    id={`gallery-sort-by-${sortOption}`}
                    data-qa-id={`gallery-sort-by-${sortOption}`}
                    disableProcessing
                  />
                ))}
              </div>
            </div>
            <Divider />
          </>
        )}
        <div className={styles.filterGroups}>
          {Object.entries(filterGroups).map(([groupKey, filterGroup]) => (
            <FilterGroup
              key={groupKey}
              groupKey={groupKey}
              data={filterGroup}
              isOpen={openGroups.has(groupKey)}
              onToggleGroup={handleGroupToggle}
              onSelectFilter={onFilterClick}
            />
          ))}
        </div>
      </div>
      {!isLargeAndUp && (
        <div className={styles.filterManagerFooter} ref={footerRef}>
          <Button
            variant="secondary"
            size="large"
            data-qa-id="galleryFiltersDrawerClearFiltersButton"
            onClick={() => onClearFilters()}
            disabled={!hasActiveFilters}
            disableProcessing
          >
            Clear filters
          </Button>
          <Button
            variant="primary"
            size="large"
            data-qa-id="galleryFiltersDrawerApplyFiltersButton"
            onClick={handleApplyClick}
          >
            {ApplyButtonText}
          </Button>
        </div>
      )}
    </>
  );
};

interface FilterManagerProps extends FilterManagerContentProps {
  onCancelChanges: () => void;
}

export const FilterManager: FC<FilterManagerProps> = ({
  onFilterClick,
  onSortByChange,
  open = false,
  onCancelChanges,
  onClearFilters,
  onApplyFilters,
  isLoading = false,
}) => {
  const isLargeAndUp = useIsLargeAndUp();
  const hasFilters = useSelector(selectHasFilters);

  if (!hasFilters) return null;

  if (!isLargeAndUp) {
    return (
      <ResponsivePanel
        renderMobileViewAs="bottom-sheet"
        isOpen={open}
        onClose={onCancelChanges}
        title="Filters & sort"
        bottomSheetHeightSetting="full"
        hideHeaderDivider
      >
        <FilterManagerContent
          onFilterClick={onFilterClick}
          onSortByChange={onSortByChange}
          open={open}
          onClearFilters={onClearFilters}
          onApplyFilters={onApplyFilters}
          isLoading={isLoading}
        />
      </ResponsivePanel>
    );
  }

  return (
    <div className={cx(styles.filterManagerWrapper, {[styles.open]: open})}>
      <FilterManagerContent
        onFilterClick={onFilterClick}
        onSortByChange={onSortByChange}
        open={open}
        onClearFilters={onClearFilters}
        onApplyFilters={onApplyFilters}
        isLoading={isLoading}
      />
    </div>
  );
};
